import { useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import TabContentRenderer from '../components/molecules/TabContentRenderer/TabContentRenderer';
import useDetailTabs from '../domain/detail/useDetailTabs';
import { useContexte } from '../hooks/useContexte';
import { useGetDetailTabs } from '../services/detail/detailQueries';
import styles from './DetailTabContent.module.css';

/** Active tab background tokens by horizontal tab level (see HorizontalTabs.module.css). */
const DETAIL_CONTENT_SURFACE_BY_LEVEL = [
  '--neutral-200',
  '--neutral-50',
  '--neutral-100',
  '--neutral-100',
];

/**
 * Detail tab content area. Receives page updates without remounting when used inside DetailLayout.
 * @param {object} props
 * @param {ApiPage} [props.page]
 * @param {boolean} [props.isLoading=false]
 * @returns {JSX.Element | null}
 */
function DetailTabContent({ page, isLoading = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const contexte = useContexte();

  const path = page?.path || '';
  const currentPath = `${location.pathname}${location.search}`;

  const { data: detailTabsData } = useGetDetailTabs(path, contexte);
  const tabs = detailTabsData?.tabs ?? [];

  const { tabRows } = useDetailTabs({
    tabs,
    currentPath,
    searchParams,
    navigate,
  });

  const detailContentSurfaceStyle = useMemo(() => {
    const deepestLevel = Math.min(Math.max(tabRows.length - 1, 0), 3);
    const surfaceToken = DETAIL_CONTENT_SURFACE_BY_LEVEL[deepestLevel];

    return /** @type {import('react').CSSProperties} */ ({
      '--detail-content-surface': `var(${surfaceToken})`,
    });
  }, [tabRows.length]);

  return (
    <div
      className={styles['detail-tab-content']}
      style={detailContentSurfaceStyle}
    >
      <TabContentRenderer
        type={page?.format}
        page={page}
        isLoading={isLoading}
      />
    </div>
  );
}

export default DetailTabContent;
